import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';

interface SecureImageProps {
  src?: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
}

export const SecureImage: React.FC<SecureImageProps> = ({
  src,
  alt,
  className = '',
  style,
  onError
}) => {
  const { keycloak } = useKeycloak();
  const [imageSrc, setImageSrc] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src || !keycloak?.token) {
      setLoading(false);
      setError(true);
      return;
    }

    const loadSecureImage = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await fetch(src, {
          headers: {
            'authorization': 'Bearer ' + keycloak.token
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      } catch (err) {
        console.error('Failed to load secure image:', err);
        setError(true);
        if (onError) onError();
      } finally {
        setLoading(false);
      }
    };

    loadSecureImage();

    // Cleanup object URL on unmount
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src, keycloak?.token]);

  if (loading) {
    return (
      <div 
        className={`flex items-center justify-center bg-[#eaeaea] rounded-full ${className}`}
        style={style}
      >
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#bbb]"></div>
      </div>
    );
  }

  if (error || !imageSrc) {
    if (onError) onError();
    return null; // Let parent component handle fallback
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      onError={() => {
        setError(true);
        if (onError) onError();
      }}
    />
  );
};
